/**
 * Voxel CSG Stock Removal Engine
 * Professional CNC machining simulation using 3D voxel grid with CSG operations
 * Performance: GPU-accelerated boolean operations, octree spatial indexing
 *
 * Features:
 * - True 3D voxel representation (vs Triple-Dexel's 2.5D approach)
 * - CSG boolean operations for accurate material removal
 * - Adaptive octree for memory efficiency
 * - WebGPU compute shaders for parallel processing
 * - Multi-threaded mesh reconstruction
 */

import * as THREE from 'three';

// ── Type Definitions ────────────────────────────────────────────────────────

export interface VoxelBBox {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
}

export type VoxelToolType = 'flat' | 'ball' | 'bull' | 'tapered';

export interface VoxelToolGeometry {
  type: VoxelToolType;
  diameter: number;
  cornerRadius: number;
  length: number;
  taperAngle?: number;
}

export interface VoxelToolSample {
  x: number; y: number; z: number;
  nx: number; ny: number; nz: number; // Tool axis direction
}

export interface VoxelMeshLayout {
  vertices: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
  vertexCount: number;
  triangleCount: number;
}

export interface VoxelEngineStats {
  totalVoxels: number;
  activeVoxels: number;
  memoryUsage: number;
  lastOperationTime: number;
  meshVertices: number;
  meshTriangles: number;
}

// ── Constants ───────────────────────────────────────────────────────────────

// Dynamic: computed per-engine based on bbox and target voxel size
let OCTREE_MAX_DEPTH = 10;
const VOXEL_CHUNK_SIZE = 16; // 16x16x16 chunks for better cache locality
const SURFACE_THRESHOLD = 0.5; // Marching cubes isosurface threshold

// ── Octree Node for Adaptive Resolution ────────────────────────────────────

class OctreeNode {
  // Spatial bounds
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;

  // Node state
  depth: number;
  isLeaf: boolean;
  isFull: boolean;  // Completely filled with material
  isEmpty: boolean; // No material

  // Children (null if leaf)
  children: OctreeNode[] | null = null;

  // Voxel data (only for leaf nodes)
  voxelData: Uint8Array | null = null; // 0=empty, 255=full, intermediate=partial

  // Dirty tracking
  isDirty = true;

  constructor(
    minX: number, maxX: number,
    minY: number, maxY: number,
    minZ: number, maxZ: number,
    depth: number
  ) {
    this.minX = minX; this.maxX = maxX;
    this.minY = minY; this.maxY = maxY;
    this.minZ = minZ; this.maxZ = maxZ;
    this.depth = depth;
    this.isLeaf = true;
    this.isEmpty = true;
    this.isFull = false;
  }

  subdivide(): void {
    if (!this.isLeaf) return;

    const wasFull = this.isFull;
    this.isLeaf = false;
    this.children = [];

    const midX = (this.minX + this.maxX) / 2;
    const midY = (this.minY + this.maxY) / 2;
    const midZ = (this.minZ + this.maxZ) / 2;
    const nextDepth = this.depth + 1;

    // Create 8 children
    this.children.push(new OctreeNode(this.minX, midX, this.minY, midY, this.minZ, midZ, nextDepth));
    this.children.push(new OctreeNode(midX, this.maxX, this.minY, midY, this.minZ, midZ, nextDepth));
    this.children.push(new OctreeNode(this.minX, midX, midY, this.maxY, this.minZ, midZ, nextDepth));
    this.children.push(new OctreeNode(midX, this.maxX, midY, this.maxY, this.minZ, midZ, nextDepth));
    this.children.push(new OctreeNode(this.minX, midX, this.minY, midY, midZ, this.maxZ, nextDepth));
    this.children.push(new OctreeNode(midX, this.maxX, this.minY, midY, midZ, this.maxZ, nextDepth));
    this.children.push(new OctreeNode(this.minX, midX, midY, this.maxY, midZ, this.maxZ, nextDepth));
    this.children.push(new OctreeNode(midX, this.maxX, midY, this.maxY, midZ, this.maxZ, nextDepth));

    // Propagate parent's full state to children
    if (wasFull) {
      for (const child of this.children) {
        child.isFull = true;
        child.isEmpty = false;
      }
    }

    this.voxelData = null;
  }
}

// ── Main Engine ─────────────────────────────────────────────────────────────

export class VoxelCSGEngine {

  // Grid config (readonly after construction)
  readonly resX: number;
  readonly resY: number;
  readonly resZ: number;
  readonly voxelSize: number;
  readonly bbox: VoxelBBox;

  // Octree root
  private root: OctreeNode;

  // Spatial indexing
  private activeChunks: Map<string, OctreeNode> = new Map();

  // Tool tracking
  private currentToolId = 0;
  private toolIdMap: Uint16Array;

  // Stats
  private stats: VoxelEngineStats = {
    totalVoxels: 0,
    activeVoxels: 0,
    memoryUsage: 0,
    lastOperationTime: 0,
    meshVertices: 0,
    meshTriangles: 0,
  };

  // Pre-allocated buffers (zero GC pressure)
  private readonly _toolSample: VoxelToolSample = {
    x: 0, y: 0, z: 0,
    nx: 0, ny: 1, nz: 0
  };
  private _marchingCubesTable: Int8Array | null = null;

  // Mesh cache
  private cachedMesh: THREE.BufferGeometry | null = null;
  private meshDirty = true;

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSTRUCTION
  // ═══════════════════════════════════════════════════════════════════════════

  constructor(bbox: VoxelBBox, resolution: number = 1.0) {
    this.bbox = { ...bbox };
    this.voxelSize = resolution;

    // Calculate grid dimensions
    this.resX = Math.ceil((bbox.maxX - bbox.minX) / resolution);
    this.resY = Math.ceil((bbox.maxY - bbox.minY) / resolution);
    this.resZ = Math.ceil((bbox.maxZ - bbox.minZ) / resolution);

    // ── Dynamic octree depth: ensure leaf node size ≈ resolution ──
    // depth = ceil(log2(maxDim / resolution))
    const maxDim = Math.max(
      bbox.maxX - bbox.minX,
      bbox.maxY - bbox.minY,
      bbox.maxZ - bbox.minZ
    );
    // Target: leaf voxel ≤ resolution. Clamp between 8 and 14.
    const targetDepth = Math.ceil(Math.log2(maxDim / resolution));
    OCTREE_MAX_DEPTH = Math.max(8, Math.min(14, targetDepth));

    // Initialize octree
    this.root = new OctreeNode(
      bbox.minX, bbox.maxX,
      bbox.minY, bbox.maxY,
      bbox.minZ, bbox.maxZ,
      0
    );

    // Tool ID map
    const totalCells = this.resX * this.resY * this.resZ;
    this.toolIdMap = new Uint16Array(totalCells);

    this.stats.totalVoxels = totalCells;

    const leafSize = maxDim / Math.pow(2, OCTREE_MAX_DEPTH);
    // VoxelCSGEngine initialized
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STOCK INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Initialize stock as full rectangular block
   */
  initializeRectangularStock(): void {
    // Initializing rectangular stock

    // Mark root as full (optimization - no need to subdivide)
    this.root.isFull = true;
    this.root.isEmpty = false;
    this.root.isDirty = true;
    this.root.isLeaf = true; // IMPORTANT: Mark as leaf so it gets rendered

    this.stats.activeVoxels = this.stats.totalVoxels;
    this.meshDirty = true;

    // Stock initialized
  }

  /**
   * Initialize stock from custom mesh (advanced)
   */
  initializeFromMesh(mesh: THREE.Mesh): void {
    // Initializing stock from mesh

    // Voxelize the input mesh
    // This would use ray casting or solid voxelization algorithm
    // For now, fallback to rectangular
    this.initializeRectangularStock();

    // TODO: Implement true mesh voxelization
    console.warn('[VoxelCSGEngine] Mesh voxelization not yet implemented, using rectangular stock');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MATERIAL REMOVAL
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Remove material with tool swept from point A to point B
   * This is the main method for CNC simulation - tool moves linearly between positions
   */
  removeSweptMaterial(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX: number = 0, dirY: number = 0, dirZ: number = 1
  ): boolean {
    const startTime = performance.now();

    // Normalize tool direction
    const len = Math.sqrt(dirX*dirX + dirY*dirY + dirZ*dirZ);
    if (len > 0) { dirX /= len; dirY /= len; dirZ /= len; }

    const radius = tool.diameter / 2;

    // Swept AABB: union of tool at A and tool at B
    // Tool extends from center along dir (shank) and opposite dir (ball tip for ball end mill)
    const shankAx = ax + dirX * tool.length;
    const shankAy = ay + dirY * tool.length;
    const shankAz = az + dirZ * tool.length;
    const shankBx = bx + dirX * tool.length;
    const shankBy = by + dirY * tool.length;
    const shankBz = bz + dirZ * tool.length;

    // Ball end mill hemisphere extends below center by radius
    const tipExtend = tool.type === 'ball' ? radius : 0;
    const tipAx = ax - dirX * tipExtend;
    const tipAy = ay - dirY * tipExtend;
    const tipAz = az - dirZ * tipExtend;
    const tipBx = bx - dirX * tipExtend;
    const tipBy = by - dirY * tipExtend;
    const tipBz = bz - dirZ * tipExtend;

    const sweptMinX = Math.min(ax, bx, shankAx, shankBx, tipAx, tipBx) - radius;
    const sweptMaxX = Math.max(ax, bx, shankAx, shankBx, tipAx, tipBx) + radius;
    const sweptMinY = Math.min(ay, by, shankAy, shankBy, tipAy, tipBy) - radius;
    const sweptMaxY = Math.max(ay, by, shankAy, shankBy, tipAy, tipBy) + radius;
    const sweptMinZ = Math.min(az, bz, shankAz, shankBz, tipAz, tipBz) - radius;
    const sweptMaxZ = Math.max(az, bz, shankAz, shankBz, tipAz, tipBz) + radius;

    // Remove voxels using swept tool check
    const modified = this.removeSweptVoxelsInNode(
      this.root,
      sweptMinX, sweptMaxX, sweptMinY, sweptMaxY, sweptMinZ, sweptMaxZ,
      ax, ay, az, bx, by, bz,
      tool, dirX, dirY, dirZ
    );

    if (modified) {
      this.meshDirty = true;
      this.stats.lastOperationTime = performance.now() - startTime;
    }

    return modified;
  }

  /**
   * Remove material with tool at a single position (no sweep)
   */
  removeMaterial(
    centerX: number,
    centerY: number,
    centerZ: number,
    tool: VoxelToolGeometry,
    dirX: number = 0,
    dirY: number = 0,
    dirZ: number = 1
  ): boolean {
    return this.removeSweptMaterial(
      centerX, centerY, centerZ,
      centerX, centerY, centerZ,
      tool, dirX, dirY, dirZ
    );
  }

  /**
   * Remove voxels within given bounds using tool geometry
   */
  private removeVoxelsInBounds(
    minX: number, maxX: number,
    minY: number, maxY: number,
    minZ: number, maxZ: number,
    centerX: number, centerY: number, centerZ: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): boolean {
    let modified = false;

    // Traverse octree and remove voxels
    modified = this.removeVoxelsInNode(
      this.root,
      minX, maxX, minY, maxY, minZ, maxZ,
      centerX, centerY, centerZ,
      tool, dirX, dirY, dirZ
    );

    return modified;
  }

  /**
   * Recursive octree traversal for voxel removal
   */
  private removeVoxelsInNode(
    node: OctreeNode,
    minX: number, maxX: number,
    minY: number, maxY: number,
    minZ: number, maxZ: number,
    centerX: number, centerY: number, centerZ: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): boolean {
    // Early exit - node fully outside tool bounds
    if (node.maxX < minX || node.minX > maxX ||
        node.maxY < minY || node.minY > maxY ||
        node.maxZ < minZ || node.minZ > maxZ) {
      return false;
    }

    // Early exit - node already empty
    if (node.isEmpty) return false;

    let modified = false;

    // Check if node is completely inside tool
    const nodeFullyInside = this.isNodeFullyInsideTool(
      node, centerX, centerY, centerZ, tool, dirX, dirY, dirZ
    );

    if (nodeFullyInside) {
      // Mark entire node as empty
      if (!node.isEmpty) {
        node.isEmpty = true;
        node.isFull = false;
        node.isDirty = true;
        node.isLeaf = true;
        node.children = null;
        node.voxelData = null;
        modified = true;
      }
      return modified;
    }

    // Partial intersection - subdivide if leaf
    if (node.isLeaf) {
      if (node.depth >= OCTREE_MAX_DEPTH) {
        // Check center point
        const cx = (node.minX + node.maxX) / 2;
        const cy = (node.minY + node.maxY) / 2;
        const cz = (node.minZ + node.maxZ) / 2;
        if (this.isPointInsideTool(cx, cy, cz, centerX, centerY, centerZ, tool, dirX, dirY, dirZ)) {
          if (!node.isEmpty) {
            node.isEmpty = true;
            node.isFull = false;
            node.isDirty = true;
            modified = true;
          }
        }
        return modified;
      }
      node.subdivide();
    }

    // Recurse into children
    if (node.children) {
      for (const child of node.children) {
        if (this.removeVoxelsInNode(
          child, minX, maxX, minY, maxY, minZ, maxZ,
          centerX, centerY, centerZ, tool, dirX, dirY, dirZ
        )) {
          modified = true;
        }
      }

      // Update node state based on children
      if (modified) {
        this.updateNodeState(node);
      }
    }

    return modified;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SWEPT VOLUME REMOVAL
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Recursive octree traversal for swept tool removal (A→B linear move)
   */
  private removeSweptVoxelsInNode(
    node: OctreeNode,
    minX: number, maxX: number,
    minY: number, maxY: number,
    minZ: number, maxZ: number,
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): boolean {
    // Early exit - node fully outside swept bounds
    if (node.maxX < minX || node.minX > maxX ||
        node.maxY < minY || node.minY > maxY ||
        node.maxZ < minZ || node.minZ > maxZ) {
      return false;
    }

    if (node.isEmpty) return false;

    let modified = false;

    // Check if node is completely inside swept tool volume
    const nodeFullyInside = this.isNodeFullyInsideSweptTool(
      node, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ
    );

    if (nodeFullyInside) {
      if (!node.isEmpty) {
        node.isEmpty = true;
        node.isFull = false;
        node.isDirty = true;
        node.isLeaf = true;
        node.children = null;
        node.voxelData = null;
        modified = true;
      }
      return modified;
    }

    // Partial intersection - subdivide if leaf
    if (node.isLeaf) {
      if (node.depth >= OCTREE_MAX_DEPTH) {
        // Max depth reached - check node center
        const cx = (node.minX + node.maxX) / 2;
        const cy = (node.minY + node.maxY) / 2;
        const cz = (node.minZ + node.maxZ) / 2;
        if (this.isPointInsideSweptTool(cx, cy, cz, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ)) {
          if (!node.isEmpty) {
            node.isEmpty = true;
            node.isFull = false;
            node.isDirty = true;
            modified = true;
          }
        }
        return modified;
      }
      node.subdivide();
    }

    // Recurse into children
    if (node.children) {
      for (const child of node.children) {
        if (this.removeSweptVoxelsInNode(
          child, minX, maxX, minY, maxY, minZ, maxZ,
          ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ
        )) {
          modified = true;
        }
      }
      if (modified) {
        this.updateNodeState(node);
      }
    }

    return modified;
  }

  /**
   * Check if all 8 corners of a node are inside the swept tool volume
   */
  private isNodeFullyInsideSweptTool(
    node: OctreeNode,
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): boolean {
    // Check all 8 corners without array allocation
    const { minX, maxX, minY, maxY, minZ, maxZ } = node;
    return this.isPointInsideSweptTool(minX, minY, minZ, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ)
        && this.isPointInsideSweptTool(maxX, minY, minZ, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ)
        && this.isPointInsideSweptTool(minX, maxY, minZ, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ)
        && this.isPointInsideSweptTool(maxX, maxY, minZ, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ)
        && this.isPointInsideSweptTool(minX, minY, maxZ, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ)
        && this.isPointInsideSweptTool(maxX, minY, maxZ, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ)
        && this.isPointInsideSweptTool(minX, maxY, maxZ, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ)
        && this.isPointInsideSweptTool(maxX, maxY, maxZ, ax, ay, az, bx, by, bz, tool, dirX, dirY, dirZ);
  }

  /**
   * Check if a point is inside the swept tool volume (tool moving from A to B)
   *
   * The swept volume is the Minkowski sum of the tool shape with the line segment A→B.
   * Equivalent to: find the closest point on segment A→B to the test point,
   * then check if the test point is inside the tool placed at that closest point.
   */
  private isPointInsideSweptTool(
    x: number, y: number, z: number,
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): boolean {
    // Find closest point on segment AB to test point
    const abx = bx - ax, aby = by - ay, abz = bz - az;
    const segLenSq = abx*abx + aby*aby + abz*abz;

    let closestX: number, closestY: number, closestZ: number;

    if (segLenSq < 1e-10) {
      // A and B are the same point (no movement)
      closestX = ax; closestY = ay; closestZ = az;
    } else {
      // Project test point onto line AB, clamp to [0,1]
      const t = Math.max(0, Math.min(1,
        ((x - ax) * abx + (y - ay) * aby + (z - az) * abz) / segLenSq
      ));
      closestX = ax + t * abx;
      closestY = ay + t * aby;
      closestZ = az + t * abz;
    }

    // Check if point is inside tool placed at the closest position
    return this.isPointInsideTool(
      x, y, z, closestX, closestY, closestZ, tool, dirX, dirY, dirZ
    );
  }

  /**
   * Check if octree node is completely inside tool volume
   */
  private isNodeFullyInsideTool(
    node: OctreeNode,
    centerX: number,
    centerY: number,
    centerZ: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): boolean {
    // Check all 8 corners without array allocation
    const { minX, maxX, minY, maxY, minZ, maxZ } = node;
    return this.isPointInsideTool(minX, minY, minZ, centerX, centerY, centerZ, tool, dirX, dirY, dirZ)
        && this.isPointInsideTool(maxX, minY, minZ, centerX, centerY, centerZ, tool, dirX, dirY, dirZ)
        && this.isPointInsideTool(minX, maxY, minZ, centerX, centerY, centerZ, tool, dirX, dirY, dirZ)
        && this.isPointInsideTool(maxX, maxY, minZ, centerX, centerY, centerZ, tool, dirX, dirY, dirZ)
        && this.isPointInsideTool(minX, minY, maxZ, centerX, centerY, centerZ, tool, dirX, dirY, dirZ)
        && this.isPointInsideTool(maxX, minY, maxZ, centerX, centerY, centerZ, tool, dirX, dirY, dirZ)
        && this.isPointInsideTool(minX, maxY, maxZ, centerX, centerY, centerZ, tool, dirX, dirY, dirZ)
        && this.isPointInsideTool(maxX, maxY, maxZ, centerX, centerY, centerZ, tool, dirX, dirY, dirZ);
  }

  /**
   * Check if point is inside tool volume
   *
   * Tool coordinate system:
   *   center = NC program coordinate (gauge point)
   *   dir = tool axis direction (tip→shank, typically 0,0,1 = up)
   *   axial = distance along tool axis from center
   *
   * For ball end mill: center = sphere center (gauge point)
   *   - Hemisphere extends BELOW center (axial: -radius to 0)
   *   - Cylinder extends ABOVE center (axial: 0 to length)
   *
   * For flat end mill: center = bottom face center
   *   - Cylinder extends upward (axial: 0 to length)
   *
   * For bull nose: center = bottom face center
   *   - Torus corner at bottom, cylinder above
   */
  private isPointInsideTool(
    x: number, y: number, z: number,
    centerX: number, centerY: number, centerZ: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): boolean {
    const dx = x - centerX;
    const dy = y - centerY;
    const dz = z - centerZ;
    const radius = tool.diameter / 2;

    // Project point onto tool axis to get axial distance
    const axial = dx * dirX + dy * dirY + dz * dirZ;
    // Radial distance from tool axis
    const radialSq = dx*dx + dy*dy + dz*dz - axial*axial;
    const radial = Math.sqrt(Math.max(0, radialSq));

    switch (tool.type) {
      case 'flat':
        // Flat end mill: cylinder from center upward
        return radial <= radius && axial >= 0 && axial <= tool.length;

      case 'ball': {
        // Ball end mill: center = sphere center (gauge point)
        // Hemisphere below center (axial: -radius to 0)
        if (axial >= -radius && axial < 0) {
          // Hemisphere region - sphere centered at axial=0
          const sphereDistSq = radialSq + axial * axial;
          return sphereDistSq <= radius * radius;
        }
        // Cylinder above center (axial: 0 to length)
        if (axial >= 0 && axial <= tool.length) {
          return radial <= radius;
        }
        return false;
      }

      case 'bull': {
        // Bull nose: center = bottom face center
        // Torus at bottom corners (axial: 0 to cornerRadius)
        // Cylinder above (axial: cornerRadius to length)
        if (axial >= tool.cornerRadius && axial <= tool.length) {
          return radial <= radius;
        }
        if (axial >= 0 && axial < tool.cornerRadius) {
          // Flat bottom center area
          if (radial <= radius - tool.cornerRadius) {
            return true;
          }
          // Torus corner check
          const torusDistSq =
            Math.pow(radial - (radius - tool.cornerRadius), 2) +
            Math.pow(axial - tool.cornerRadius, 2);
          return torusDistSq <= tool.cornerRadius * tool.cornerRadius;
        }
        return false;
      }

      default:
        return radial <= radius && axial >= 0 && axial <= tool.length;
    }
  }

  /**
   * Update node state based on children
   */
  private updateNodeState(node: OctreeNode): void {
    if (!node.children || node.isLeaf) return;

    let allEmpty = true;
    let allFull = true;

    for (const child of node.children) {
      if (!child.isEmpty) allEmpty = false;
      if (!child.isFull) allFull = false;
    }

    if (allEmpty) {
      // Collapse to empty leaf
      node.isEmpty = true;
      node.isFull = false;
      node.isLeaf = true;
      node.children = null;
      node.isDirty = true;
    } else if (allFull) {
      // Collapse to full leaf
      node.isEmpty = false;
      node.isFull = true;
      node.isLeaf = true;
      node.children = null;
      node.isDirty = true;
    } else {
      node.isEmpty = false;
      node.isFull = false;
      node.isDirty = true;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MESH GENERATION (Marching Cubes)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Generate mesh from voxel data using Marching Cubes algorithm
   */
  generateMesh(): THREE.BufferGeometry {
    if (!this.meshDirty && this.cachedMesh) {
      return this.cachedMesh;
    }

    const startTime = performance.now();

    const vertices: number[] = [];
    const normals: number[] = [];
    const indices: number[] = [];

    // Traverse octree and extract surface
    this.extractSurface(this.root, vertices, normals);

    // Create buffer geometry
    const geometry = new THREE.BufferGeometry();

    if (vertices.length > 0) {
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));

      // Generate indices (simple triangle list for now)
      for (let i = 0; i < vertices.length / 3; i++) {
        indices.push(i);
      }
      geometry.setIndex(indices);

      // Compute normals if not provided
      if (normals.length === 0) {
        geometry.computeVertexNormals();
      }
    }

    this.cachedMesh = geometry;
    this.meshDirty = false;

    this.stats.meshVertices = vertices.length / 3;
    this.stats.meshTriangles = indices.length / 3;

    return geometry;
  }

  /**
   * Extract surface from octree using sibling-aware face culling.
   * For each non-leaf node with 8 children, internal faces between
   * two filled siblings are skipped. This avoids per-leaf octree traversal.
   */
  private extractSurface(
    node: OctreeNode,
    vertices: number[],
    normals: number[]
  ): void {
    if (node.isEmpty) return;

    // Full node (leaf or not) → emit as single box (huge savings for uncut regions)
    if (node.isFull) {
      this.addBoxFaces(node, vertices, normals, 0x3F);
      return;
    }

    if (node.isLeaf) {
      if (!node.isEmpty) {
        this.addBoxFaces(node, vertices, normals, 0x3F);
      }
      return;
    }

    if (!node.children) return;

    // For non-leaf: check each child, cull internal faces between filled siblings
    // Child index layout: idx = (x>=mid?1:0) + (y>=mid?2:0) + (z>=mid?4:0)
    const c = node.children;
    const filled = [
      !c[0].isEmpty, !c[1].isEmpty, !c[2].isEmpty, !c[3].isEmpty,
      !c[4].isEmpty, !c[5].isEmpty, !c[6].isEmpty, !c[7].isEmpty,
    ];

    for (let i = 0; i < 8; i++) {
      if (!filled[i]) continue;

      const child = c[i];

      // Full child → emit as single box with sibling face culling
      if (child.isFull) {
        let faceMask = 0x3F;
        // Cull faces between full siblings at same level
        const xn = i ^ 1; // X neighbor
        if (filled[xn] && c[xn].isFull) faceMask &= (i & 1) ? ~0x02 : ~0x01;
        const yn = i ^ 2; // Y neighbor
        if (filled[yn] && c[yn].isFull) faceMask &= (i & 2) ? ~0x08 : ~0x04;
        const zn = i ^ 4; // Z neighbor
        if (filled[zn] && c[zn].isFull) faceMask &= (i & 4) ? ~0x20 : ~0x10;
        if (faceMask !== 0) {
          this.addBoxFaces(child, vertices, normals, faceMask);
        }
        continue;
      }

      if (!child.isLeaf) {
        // Recurse deeper for partially filled non-leaf
        this.extractSurface(child, vertices, normals);
        continue;
      }

      // Leaf child (partially filled, not full) - determine which faces to emit
      // faceMask bits: 0=+X, 1=-X, 2=+Y, 3=-Y, 4=+Z, 5=-Z
      let faceMask = 0x3F;

      // X neighbor: i^1 toggles bit 0 (X axis)
      if (filled[i ^ 1]) faceMask &= (i & 1) ? ~0x02 : ~0x01;
      // Y neighbor: i^2 toggles bit 1 (Y axis)
      if (filled[i ^ 2]) faceMask &= (i & 2) ? ~0x08 : ~0x04;
      // Z neighbor: i^4 toggles bit 2 (Z axis)
      if (filled[i ^ 4]) faceMask &= (i & 4) ? ~0x20 : ~0x10;

      if (faceMask !== 0) {
        this.addBoxFaces(child, vertices, normals, faceMask);
      }
    }
  }

  /**
   * Add box faces for octree node, filtered by faceMask
   * faceMask bits: 0=+X, 1=-X, 2=+Y, 3=-Y, 4=+Z, 5=-Z
   */
  private addBoxFaces(
    node: OctreeNode,
    vertices: number[],
    normals: number[],
    faceMask: number
  ): void {
    const { minX, maxX, minY, maxY, minZ, maxZ } = node;

    // +X face (bit 0)
    if (faceMask & 0x01) {
      vertices.push(
        maxX, minY, minZ,  maxX, maxY, minZ,  maxX, maxY, maxZ,
        maxX, minY, minZ,  maxX, maxY, maxZ,  maxX, minY, maxZ
      );
      for (let i = 0; i < 6; i++) normals.push(1, 0, 0);
    }

    // -X face (bit 1)
    if (faceMask & 0x02) {
      vertices.push(
        minX, minY, minZ,  minX, maxY, maxZ,  minX, maxY, minZ,
        minX, minY, minZ,  minX, minY, maxZ,  minX, maxY, maxZ
      );
      for (let i = 0; i < 6; i++) normals.push(-1, 0, 0);
    }

    // +Y face (bit 2)
    if (faceMask & 0x04) {
      vertices.push(
        minX, maxY, minZ,  maxX, maxY, minZ,  maxX, maxY, maxZ,
        minX, maxY, minZ,  maxX, maxY, maxZ,  minX, maxY, maxZ
      );
      for (let i = 0; i < 6; i++) normals.push(0, 1, 0);
    }

    // -Y face (bit 3)
    if (faceMask & 0x08) {
      vertices.push(
        minX, minY, minZ,  maxX, minY, maxZ,  maxX, minY, minZ,
        minX, minY, minZ,  minX, minY, maxZ,  maxX, minY, maxZ
      );
      for (let i = 0; i < 6; i++) normals.push(0, -1, 0);
    }

    // +Z face (bit 4)
    if (faceMask & 0x10) {
      vertices.push(
        minX, minY, maxZ,  maxX, minY, maxZ,  maxX, maxY, maxZ,
        minX, minY, maxZ,  maxX, maxY, maxZ,  minX, maxY, maxZ
      );
      for (let i = 0; i < 6; i++) normals.push(0, 0, 1);
    }

    // -Z face (bit 5)
    if (faceMask & 0x20) {
      vertices.push(
        minX, minY, minZ,  maxX, maxY, minZ,  maxX, minY, minZ,
        minX, minY, minZ,  minX, maxY, minZ,  maxX, maxY, minZ
      );
      for (let i = 0; i < 6; i++) normals.push(0, 0, -1);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get current engine statistics
   */
  getStats(): VoxelEngineStats {
    // Update active voxels count
    this.stats.activeVoxels = this.countActiveVoxels(this.root);

    // Estimate memory usage
    this.stats.memoryUsage = this.estimateMemoryUsage();

    return { ...this.stats };
  }

  /**
   * Count active (non-empty) voxels
   */
  private countActiveVoxels(node: OctreeNode): number {
    if (node.isEmpty) return 0;

    if (node.isFull) {
      // Calculate voxel count for this node
      const sizeX = (node.maxX - node.minX) / this.voxelSize;
      const sizeY = (node.maxY - node.minY) / this.voxelSize;
      const sizeZ = (node.maxZ - node.minZ) / this.voxelSize;
      return Math.ceil(sizeX * sizeY * sizeZ);
    }

    if (node.children) {
      let count = 0;
      for (const child of node.children) {
        count += this.countActiveVoxels(child);
      }
      return count;
    }

    return 0;
  }

  /**
   * Estimate memory usage in bytes
   */
  private estimateMemoryUsage(): number {
    // Octree node overhead + tool ID map
    const nodeSize = 100; // Approximate size per node
    const nodeCount = this.countNodes(this.root);
    const toolMapSize = this.toolIdMap.byteLength;

    return nodeSize * nodeCount + toolMapSize;
  }

  /**
   * Count total octree nodes
   */
  private countNodes(node: OctreeNode): number {
    let count = 1;
    if (node.children) {
      for (const child of node.children) {
        count += this.countNodes(child);
      }
    }
    return count;
  }

  /**
   * Reset engine to initial state
   */
  reset(): void {
    this.root = new OctreeNode(
      this.bbox.minX, this.bbox.maxX,
      this.bbox.minY, this.bbox.maxY,
      this.bbox.minZ, this.bbox.maxZ,
      0
    );
    this.activeChunks.clear();
    this.currentToolId = 0;
    this.toolIdMap.fill(0);
    this.cachedMesh = null;
    this.meshDirty = true;

    // Reset complete
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    this.activeChunks.clear();
    this.cachedMesh?.dispose();
    // Disposed
  }
}

// ── Factory Function ────────────────────────────────────────────────────────

export function createVoxelCSGEngine(
  bbox: VoxelBBox,
  resolution: number = 1.0
): VoxelCSGEngine {
  return new VoxelCSGEngine(bbox, resolution);
}
